// @ts-strict-ignore
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AggregationEditorContext } from './context';
import AggregationsSubEditorContainer from './AggregationsSubEditorContainer';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { selectWorkflowData, selectWorkflowProps } from 'flow/store/redux/selectors';
import { getAggregationConfigForAttribute } from 'flow/store/engine/update/helpers';
import { populateDataRouteData } from 'flow/service/dataRoutes/dataRoutes';
import type { AggregationSourceData } from './types';
import type { DataRoute } from 'flow/store/engine/types';
import {
  aggregationComponentsToSourceData,
  sourceDataToAggregationComponents,
} from './transformers';
import { getIsSaveDisabled } from './helpers';
import { updateDataRouteValue } from 'flow/store/redux/slice';
import { RuleEditorContext } from '../NodeEditorDrawer/context';
import { getMultiSelectProps } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/helpers';
import { datadogRum } from '@datadog/browser-rum';
import { useKeyboardFocus, useSnackBar } from '@usealloy/component-library';
import { keyBinds } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/constants';
import { INACTIVE_AGGREGATION_OBJECT_MESSAGE } from './constants';

interface AggregationDataRouteWrapperProps {
  dataRoute: DataRoute;
}

const AggregationDataRouteWrapper = ({ dataRoute }: AggregationDataRouteWrapperProps) => {
  const dispatch = useAppDispatch();
  const { actions } = useAppSelector(selectWorkflowData);
  const {
    services,
    aggregationConfig: aggregationConfigByObjectType,
    universalAttributes,
  } = useAppSelector(selectWorkflowProps);
  const { setIsolatedEditingBlock } = useContext(RuleEditorContext);
  const addSnackBar = useSnackBar();

  const populatedDataRoute = useMemo(
    () => populateDataRouteData({
      actions, services, dataRoute, universalAttributes,
    }),
    [actions, services, dataRoute, universalAttributes],
  );

  const attribute = populatedDataRoute?.attribute?.details;

  const aggregationConfig = useMemo(
    () => (
      attribute
        ? getAggregationConfigForAttribute(attribute, aggregationConfigByObjectType)
        : undefined
    ),
    [attribute, aggregationConfigByObjectType],
  );

  const aggregationEditorContainerRef = useRef<HTMLDivElement>(null);
  const [sourceData, setSourceData] = useState<AggregationSourceData>(
    () => aggregationComponentsToSourceData(dataRoute.aggregationComponents),
  );

  const isSaveDisabled = useMemo(
    () => getIsSaveDisabled(sourceData),
    [sourceData],
  );

  const keyboardFocus = useKeyboardFocus({
    containerRef: aggregationEditorContainerRef,
    keyBinds,
    onError: (error, context) => datadogRum.addError(error, context),
  });

  const aggregationEditorContext = useMemo(() => {
    const handleClose = () => {
      setIsolatedEditingBlock({
        originPublicId: undefined,
        originType: undefined,
        originCaretIndex: undefined,
      });
    };

    return {
      sourceData,
      setSourceData,
      handleSave: () => {
        const existingComponents = populatedDataRoute.aggregationComponents;
        const withFallbacks = [{
          objectSlug: aggregationConfig?.objectType,
          objectVersionPublicVersion: aggregationConfig?.publicVersion,
          ...existingComponents[0],
        }, ...existingComponents.slice(1)];
        dispatch(updateDataRouteValue({
          path: `${populatedDataRoute.publicId}.aggregationComponents`,
          value: sourceDataToAggregationComponents(sourceData, withFallbacks),
        }));
        handleClose();
      },
      handleCancel: () => {
        setSourceData(aggregationComponentsToSourceData(
          populatedDataRoute.aggregationComponents,
        ));
        handleClose();
      },
      aggregationEditorContainerRef,
      multiSelectProps: getMultiSelectProps({
        action: populatedDataRoute?.sourceAction,
        options: { isPreview: false },
      }),
      aggregationConfig,
      isSaveDisabled,
      validationErrors: {},
      keyboardFocus,
    };
  }, [
    sourceData,
    setIsolatedEditingBlock,
    dispatch,
    populatedDataRoute,
    aggregationConfig,
    isSaveDisabled,
    keyboardFocus,
  ]);

  // The object's active version isn't selectable (e.g. an existing route on an
  // object that has since been deactivated). Close the editor and explain why
  // rather than rendering a broken/blank editor.
  useEffect(() => {
    if (!aggregationConfig) {
      addSnackBar({ title: INACTIVE_AGGREGATION_OBJECT_MESSAGE, type: 'danger' });
      setIsolatedEditingBlock({
        originPublicId: undefined,
        originType: undefined,
        originCaretIndex: undefined,
      });
    }
  }, [aggregationConfig, addSnackBar, setIsolatedEditingBlock]);

  if (!aggregationConfig) return null;

  return (
    <AggregationEditorContext.Provider value={aggregationEditorContext}>
      <AggregationsSubEditorContainer />
    </AggregationEditorContext.Provider>
  );
};

export default AggregationDataRouteWrapper;
